import { useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'

import { Box, Loading, NotFound } from '@factorialco/deprecated-design-system'
import { ProjectManagementProjectDetailsPagePeoplePageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import ProjectWorkersPanel from 'modules/project_management/components/ProjectWorkersPanel'
import ProjectEmployeesDataTable from 'modules/project_management/components/tables/ProjectEmployeesDataTable'
import { IProjectWorkersPanelData } from 'modules/project_management/components/tables/ProjectEmployeesDataTable/types'
import ProjectPeopleDataCollection from 'modules/project_management/components/tables/ProjectPeopleDataCollection'

const ProjectEmployeesPage = () => {
  const { id } = useParams()
  const projectId = Number(id)

  const [showProjectWorkersPanel, setShowProjectWorkersPanel] = useState<boolean>(false)
  const [projectWorkersPanelData, setProjectWorkersPanelData] = useState<IProjectWorkersPanelData>({
    projectWorkerId: 0,
    employeeAccessAvatar: undefined,
    employeeFullName: undefined,
    imputedMinutes: undefined,
    laborCostCents: undefined,
    laborCostCurrency: '',
  })

  const {
    company: { id: companyId },
    employee,
  } = useCurrentSession()

  const { data, loading } = useQuery(ProjectManagementProjectDetailsPagePeoplePageDocument, {
    variables: { companyId, projectId },
  })

  const { hasCost, hasExpenses, hasFlexibleTracking } = useOutletContext<{
    hasCost: boolean
    hasExpenses: boolean
    hasFlexibleTracking: boolean
  }>()

  useEffect(() => {
    track(trackingEvents.projectManagement.projects.goToProjectDetailsEmployees)
  }, [])

  if (loading) {
    return <Loading />
  }

  const project = data?.projectManagement.project

  if (!project) {
    return (
      <NotFound
        title={i18n.t('errors.page_not_found.title')}
        description={i18n.t('errors.page_not_found.description')}
      />
    )
  }

  const dataCollectionsEnabled =
    data?.features.featuresConnection.nodes.find(
      (node) => node.name === 'dev_projects_data_collections'
    )?.enabled ?? false

  const projectCurrency = project.legalEntity.currency

  const canManageProjects = data?.projectManagement._policies.projectManage.can || false

  const canAssignEmployees = project?._policies?.assignUnassignProjectWorkers.can || false
  const canSeeLaborCosts = project?._policies?.seeLaborCost.can || false
  const isOwner = project?._policies?.assignOwner.can || false

  const hasTimeTrackingSection =
    data?.attendance._policies.sidebarSeeAttendanceCompanyTopLevelItem.can || false

  const canExportData =
    canManageProjects &&
    !!employee &&
    (hasTimeTrackingSection || hasExpenses || hasFlexibleTracking)

  const canSeeSpendings = isOwner && hasExpenses && hasCost
  const canAssignSingleEmployee = canAssignEmployees && project.employeesAssignment !== 'company'

  const openProjectWorkersPanel = (values: IProjectWorkersPanelData) => {
    setShowProjectWorkersPanel(true)
    setProjectWorkersPanelData({
      ...values,
    })

    track(trackingEvents.projectManagement.projectWorkers.openProjectWorkersPanel, {
      is_owner: isOwner,
      can_see_costs: canSeeLaborCosts,
    })
  }

  return (
    <Box gap="s24">
      {dataCollectionsEnabled ? (
        <ProjectPeopleDataCollection
          projectCurrency={projectCurrency}
          canAssignEmployees={canAssignEmployees}
          canExportData={canExportData}
          canSeeLaborCosts={canSeeLaborCosts}
          canSeeSpendings={canSeeSpendings}
          onClick={openProjectWorkersPanel}
        />
      ) : (
        <ProjectEmployeesDataTable
          canAssignEmployees={canAssignEmployees}
          canAssignSingleEmployee={canAssignSingleEmployee}
          canExportData={canExportData}
          canSeeLaborCosts={canSeeLaborCosts}
          canSeeSpendings={canSeeSpendings}
          projectId={projectId}
          projectCurrency={projectCurrency}
          showProjectWorkersPanel={openProjectWorkersPanel}
        />
      )}
      <ProjectWorkersPanel
        projectWorkerId={projectWorkersPanelData.projectWorkerId}
        employeeAccessAvatar={projectWorkersPanelData.employeeAccessAvatar}
        employeeFullName={projectWorkersPanelData.employeeFullName}
        imputedMinutes={projectWorkersPanelData.imputedMinutes}
        laborCostCents={projectWorkersPanelData.laborCostCents}
        laborCostCurrency={projectWorkersPanelData.laborCostCurrency}
        onClose={() => {
          setShowProjectWorkersPanel(false)
        }}
        showProjectWorkersPanel={showProjectWorkersPanel}
      />
    </Box>
  )
}

export default ProjectEmployeesPage
